// react
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'router';

// llama
import { Poa as PoaPage } from '@karehero/llama/pwa/pages/Poa/Poa';

// store
import { useSelector } from 'store';
import { getAccount } from 'store/slices/iamSlice';

// hooks
import { useOpenUrl } from 'hooks/useOpenUrl';

// config
import { GOV_UK_LPA_URL, getZencoUrlForOrg, isFirstPoaFree } from 'config/poa';

export interface PoaProps {
  /** Which screen to render — both POA routes mount this one view. */
  screen: 'home' | 'pricing';
}

/**
 * Poa wires the single Power of Attorney page to both routes: the hub-style
 * home screen (`/power-of-attorney`) and the pricing calculator
 * (`/power-of-attorney/pricing`). It resolves whether the user's first online
 * review is free (account-level entitlement or partner org) and opens Zenco /
 * GOV.UK in the system browser. Back always returns home.
 */
export const Poa = ({ screen }: PoaProps) => {
  const navigate = useNavigate();
  const account = useSelector(getAccount);
  const { openExternalUrl } = useOpenUrl();

  const isFirstReviewFree = isFirstPoaFree(
    account?.organization?.name,
    account?.isPoaFreeReview,
  );
  const zencoUrl = getZencoUrlForOrg(account?.organization?.name);

  const onBack = useCallback(() => {
    navigate(ROUTES.HOME);
  }, [navigate]);

  const onApply = useCallback(() => {
    navigate(ROUTES.POA_PRICING);
  }, [navigate]);

  const onBookCall = useCallback(() => {
    navigate(ROUTES.BOOK_CALL);
  }, [navigate]);

  const onContinueToZenco = useCallback(() => {
    openExternalUrl(zencoUrl);
  }, [openExternalUrl, zencoUrl]);

  const onApplyGovUk = useCallback(() => {
    openExternalUrl(GOV_UK_LPA_URL);
  }, [openExternalUrl]);

  return (
    <PoaPage
      screen={screen}
      onBack={onBack}
      onApply={onApply}
      onBookCall={onBookCall}
      onReadyToApply={onApply}
      onTalkToTeam={onBookCall}
      isFirstReviewFree={isFirstReviewFree}
      onContinueToZenco={onContinueToZenco}
      onApplyGovUk={onApplyGovUk}
    />
  );
};

Poa.displayName = 'Poa';
export default Poa;
